import React from "react";
import {
  Box,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  useTheme,
} from "@mui/material";
import { ArrowForwardIos as ArrowForwardIosIcon } from "@mui/icons-material";
import { RiskScoreChip } from "../../components/common/RiskScoreChip";
import ReusableTable from "../../../../../components/ReusableTable/ReusableTable";
import { EmptyFields } from "../../../../../utils/enums";
import { TreatmentStatus } from "../../utils/enums";

const RiskAssessmentTable = ({
  risks,
  onViewDetails,
  refetch,
  enableHorizontalScroll = false,
}) => {
  const theme = useTheme();
  const columns = [
    { label: "Risk ID", field: "risk_key", align: "left", minWidth: 70, width: 70 },
    { label: "Risk Name", field: "risk_title", align: "left", minWidth: 180, width: 250 },
    {
      label: "Category",
      field: "risk_category_title",
      align: "left",
      minWidth: 140,
      width: 160,
    },
    { label: "Owner", field: "risk_owner_email", align: "left", minWidth: 150, width: 190 },
    {
      label: "Inherent Impact",
      field: "risk_inherent_impact",
      align: "left",
      width: 110,
    },
    {
      label: "Inherent Likelihood",
      field: "risk_inherent_likelihood",
      align: "left",
      minWidth: 130,
      width: 130,
    },
    {
      label: "Inherent Score",
      field: "inherent_risk_score",
      align: "left",
      minWidth: 110,
      width: 110,
    },
    {
      label: "Residual Score",
      field: "residual_risk_score",
      align: "left",
      minWidth: 110,
      width: 110,
    },
    { label: "Controls", field: "controls_count", align: "left", minWidth: 80, width: 80 },
    {
      label: "Treatment",
      field: "treatment_title",
      align: "left",
      minWidth: 100,
      width: 110,
    },
    { label: "Actions", field: "actions", align: "left", minWidth: 80, width: 80 },
  ];

  const renderCell = (row, column) => {
    switch (column?.field) {
      case "risk_title":
        return (
          <Tooltip title={row?.risk_description} arrow>
            <Typography
              variant="body2"
              sx={{
                maxWidth: "400px",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "wrap",
                textAlign: "left",
                width: "100%",
                color: theme.customColors.text.strong,
              }}
            >
              {row?.risk_title || EmptyFields.TITLE}
            </Typography>
          </Tooltip>
        );
      case "inherent_risk_score":
        return (
          <Box sx={{ textAlign: "left", width: "100%" }}>
            <RiskScoreChip score={row?.inherent_risk_score} type="inherent" />
          </Box>
        );
      case "residual_risk_score":
        return (
          <Box sx={{ textAlign: "left", width: "100%" }}>
            <RiskScoreChip score={row?.residual_risk_score} type="residual" />
          </Box>
        );
      case "treatment_title":
        return (
          <Box sx={{ textAlign: "left", width: "100%" }}>
            <Chip
              label={row?.treatment_title}
              size="small"
              variant="outlined"
              sx={{
                fontSize: "0.75rem",
                backgroundColor:
                  row?.treatment_title === TreatmentStatus.ACCEPTED
                    ? theme.customColors.status.success.bg
                    : theme.customColors.status.info.bg,
                color:
                  row?.treatment_title === TreatmentStatus.ACCEPTED
                    ? theme.customColors.status.success.text
                    : theme.customColors.status.info.text,
                maxWidth: 110,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                borderRadius: 0,
                border: "none",
                fontWeight: 600,
              }}
            />
          </Box>
        );
      case "actions":
        return (
          <Box sx={{ textAlign: "left", width: "100%" }}>
            <Tooltip title="View Details" arrow>
              <IconButton
                size="small"
                onClick={() => {
                  onViewDetails(row);
                  refetch();
                }}
                sx={{
                  color: theme.customColors.icons.dark,
                  "&:hover": {
                    backgroundColor: theme.palette._blue.light,
                    transform: "translateX(2px)",
                    transition: "all 0.2s ease",
                  },
                }}
              >
                <ArrowForwardIosIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          </Box>
        );
      case "risk_owner_email":
        return (
          <Tooltip title={row?.risk_owner_email || EmptyFields.OWNER} arrow>
            <Typography
              variant="body2"
              sx={{
                color: theme.customColors.text.strong,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {row?.risk_owner_email || EmptyFields.OWNER}
            </Typography>
          </Tooltip>
        );
      default:
        return (
          <Box sx={{ textAlign: "left", width: "100%" }}>
            <Typography
              variant="body2"
              sx={{ color: theme.customColors.text.strong }}
            >
              {row[column?.field]}
            </Typography>
          </Box>
        );
    }
  };

  if (!risks || risks?.length === 0) {
    return (
      <Box sx={{ textAlign: "center", py: 4 }}>
        <Typography
          variant="body2"
          sx={{ color: theme.customColors.text.secondary }}
        >
          No risks available for this assessment
        </Typography>
      </Box>
    );
  }

  return (
    <ReusableTable
      columns={columns}
      data={risks}
      renderCell={renderCell}
      rowsPerPageOptions={[10, 20, 50, 100]}
      height="auto"
      showPagination={true}
      enableHorizontalScroll={enableHorizontalScroll}
    />
  );
};

export default RiskAssessmentTable;
